import { Promise as BluebirdPromise } from 'bluebird';
import cns from 'classnames';
import { findIndex, get, isEqual } from 'lodash';
import path from 'path';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { OverlayTrigger, Popover, Row, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Route, withRouter } from 'react-router-dom';
import {
    arrayPush,
    arrayRemove,
    arraySwap,
    change,
    Field,
    FieldArray,
    getFormValues,
    initialize,
    isDirty,
    reduxForm,
    submit
} from 'redux-form';
import { v4 as uuid } from 'uuid';
import { RuntimeContext } from '../../../../../../common/RuntimeContext';
import {
    ASSET_METADATA_CATEGORIES,
    ASSET_METADATA_LOOKUP,
    CREATED_BY_TYPES,
    DEFAULT_IMAGE_URLS,
    LICENSE_TYPES,
    LEARNING_ASSET_TYPES,
    MODALS
} from '../../../../../shared/Constants';
import { makeGetObjectByKeyArrays, makeGetObjectsFromStateByFunction } from '../../../../../shared/selectors';
import masterValidate from '../../../../../shared/utils/BuilderUtils';
import { attachCloudinaryOptionsToImageSource } from '../../../../../shared/utils/ImageUtils';
import { isLearningAssetCourseType } from '../../../../../shared/utils/LearningAssetUtils';
import PromptsTab from './BuilderTabs/PromptsTab';
import SummaryTab from './BuilderTabs/SummaryTab';

import {
    clearS3Folder,
    getLearningAsset,
    getLearningAssetsHTMLExports,
    getLearningAssetStatus,
    getLearningFramework,
    getManyCollections,
    getManyGlobalSectionItems,
    getManyLearningAssets,
    hideModal,
    htmlFilePost,
    htmlMediaFilePost,
    postGlobalSectionItem,
    showModal
} from '../../../../../shared/actions/index';

import {
    getManyAssetReviewItems,
    postAssetReviewItem,
    putAssetReviewItem,
    putManyAssetReviewItems
} from '../../../../../shared/actions/AssetReviewItems';

// Inputs
import BasicSelect from '../../../../../shared/components/formComponents/groups/BasicSelect';
import BasicText from '../../../../../shared/components/formComponents/groups/BasicText';
import BasicTextArea from '../../../../../shared/components/formComponents/groups/BasicTextArea';
import BasicToggle from '../../../../../shared/components/formComponents/groups/BasicToggle';
import BasicUrlResource from '../../../../../shared/components/formComponents/groups/BasicUrlResource';
import DisplayOnlyInput from '../../../../../shared/components/formComponents/inputs/DisplayOnlyInput';

// Components
import CommentsErrorBoundary from './CommentsErrorBoundary';
import RenderComments from './RenderComments';
import RenderSections from './RenderSections';
import SectionItemFormSection from './SectionItemFormSection';

// Constants
import FormFeedback from '../../../../../shared/components/formComponents/FormFeedback';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../shared/components/VerbButton';

const SECTIONS_KEY = 'sections';
const sectionIcons = [
    { label: 'Asterisk', value: 'asterisk' },
    { label: 'Flash', value: 'bolt' }
];

export const generateAssetTypeOptions = (currentAssetType) => {
    const basicAssets = [
        'ARTICLE',
        'VIDEO',
        'ACTIVITY',
        'PRE_ASSESSMENT',
        'POST_ASSESSMENT',
        'ASSESSMENT',
        'MOBILE',
        'PRACTICAL_APPLICATION',
        'REFLECTION'
    ];
    const specialtyAssets = ['COURSE', 'IMPACT_PROJECT'];
    if (basicAssets.includes(currentAssetType)) {
        return [
            { label: 'Article', value: 'ARTICLE' },
            { label: 'Interactive', value: 'ACTIVITY' },
            { label: 'Assessment', value: 'ASSESSMENT' },
            { label: 'Pre-Assessment', value: 'PRE_ASSESSMENT' },
            { label: 'Post-Assessment', value: 'POST_ASSESSMENT' },
            { label: 'Video', value: 'VIDEO' },
            { label: 'Mobile', value: 'MOBILE' },
            { label: 'Practical Application', value: 'PRACTICAL_APPLICATION' },
            { label: 'Reflection', value: 'REFLECTION' }
        ];
    }
    if (specialtyAssets.includes(currentAssetType)) {
        return [
            { label: 'Course', value: 'COURSE' },
        ];
    }
    if (currentAssetType === 'FEEDBACK') {
        return [{ label: 'Feedback', value: 'FEEDBACK' }];
    }
    if (currentAssetType === 'TRACK') {
        return [{ label: 'Track', value: 'TRACK' }];
    }
    if (currentAssetType === 'WELCOME') {
        return [{ label: 'Welcome', value: 'WELCOME' }];
    }
    if (currentAssetType === 'SINGLE_PAGE_ACTIVITY') {
        return [{ label: 'Single Page Activity', value: 'SINGLE_PAGE_ACTIVITY' }];
    }
    return [];
};

class TrackV2Form extends Component {
    static propTypes = {
        isNew: PropTypes.bool.isRequired,
        type: PropTypes.oneOf([
            LEARNING_ASSET_TYPES.TRACK,
            LEARNING_ASSET_TYPES.ACTIVITY,
            LEARNING_ASSET_TYPES.SINGLE_PAGE_ACTIVITY,
            LEARNING_ASSET_TYPES.ARTICLE,
            LEARNING_ASSET_TYPES.VIDEO,
            LEARNING_ASSET_TYPES.IMPACT_PROJECT,
            LEARNING_ASSET_TYPES.ASSESSMENT,
            LEARNING_ASSET_TYPES.PRE_ASSESSMENT,
            LEARNING_ASSET_TYPES.POST_ASSESSMENT,
            LEARNING_ASSET_TYPES.FEEDBACK,
            LEARNING_ASSET_TYPES.WELCOME,
            LEARNING_ASSET_TYPES.MOBILE,
            LEARNING_ASSET_TYPES.PRACTICAL_APPLICATION,
            LEARNING_ASSET_TYPES.REFLECTION
        ]).isRequired,
        currentLearningAsset: PropTypes.object.isRequired,
        handleSubmit: PropTypes.func.isRequired,
        onCancel: PropTypes.func.isRequired,
        submitting: PropTypes.bool.isRequired,
        submitForm: PropTypes.func.isRequired,
        currentValues: PropTypes.object,
        initialValues: PropTypes.object,
        pristine: PropTypes.bool,
        initialize: PropTypes.func.isRequired,
        removeFromArray: PropTypes.func.isRequired,
        showPromptModal: PropTypes.func.isRequired,
        hideModal: PropTypes.func.isRequired,
        swapItem: PropTypes.func.isRequired,
        changeFormValue: PropTypes.func.isRequired,
        addToArray: PropTypes.func.isRequired,
        onSubmit: PropTypes.func,
        match: PropTypes.object.isRequired,
        reviewItems: PropTypes.object,
        sectionsByItemId: PropTypes.object,

        location: PropTypes.object.isRequired,
        behaviors: PropTypes.array,
        abilities: PropTypes.array,
        dimensions: PropTypes.array,
        behaviorsById: PropTypes.object,
        abilitiesById: PropTypes.object,
        dimensionsById: PropTypes.object,
        history: PropTypes.object.isRequired,
        isVerbAdmin: PropTypes.bool,
        isContentAuthor: PropTypes.bool,
        isContentAdmin: PropTypes.bool,
        isLearningAsset: PropTypes.bool,
        assetLinkOptions: PropTypes.array,
        getManyLearningAssets: PropTypes.func,
        getLearningAsset: PropTypes.func,
        getManyGlobalSectionItems: PropTypes.func,
        postAssetReviewItem: PropTypes.func,
        getLearningFramework: PropTypes.func,
        getManyAssetReviewItems: PropTypes.func,
        putAssetReviewItem: PropTypes.func,
        putManyAssetReviewItems: PropTypes.func,
        postGlobalSectionItem: PropTypes.func,
        globalSectionItems: PropTypes.array,
        dirty: PropTypes.bool,
        assetTypeOptions: PropTypes.array,
        isWelcomeAsset: PropTypes.bool,
        loggedInUserId: PropTypes.string,
        cleanPrompts: PropTypes.func,
        prompts: PropTypes.array,
        error: PropTypes.string,
        loggedInUser: PropTypes.object,
        setIsFormDirty: PropTypes.func,
        getManyCollections: PropTypes.func,
        collectionsByLearningAssetId: PropTypes.object
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        const availableRoutes = [
            {
                tabName: 'Summary',
                path: `${this._getBasePath(props)}/summary`,
                relativePath: '/summary',
                exact: true,
                render: this.summaryTab
            },
            {
                tabName: 'Builder',
                path: `${this._getBasePath(props)}/builder`,
                relativePath: '/builder',
                exact: true,
                render: this.builderTab
            },
            {
                tabName: 'Prompts',
                path: `${this._getBasePath(props)}/prompts`,
                relativePath: '/prompts',
                exact: true,
                render: this.promptsTab
            },
            {
                tabName: 'Comments',
                path: `${this._getBasePath(props)}/comments/${props.match.params.sectionitemid}`,
                relativePath: '/comments/:sectionitemid',
                exact: true,
                render: this.commentsTab
            }
        ];

        this.state = {
            formSaving: false,
            selectedSectionItemId: '',
            selectedSectionId: '',
            availableRoutes,
            behaviors: [],
            masterValidationMessages: [],
            currentLearningAssetStatus: {},
            hasError: false,
            files: [],
            exporting: false,
            exportStatus: '',
            participantsDataSource: [],
            failToExportFiles: [],
            globalAddSuccessMessage: '',
            globalSubmitSucceeded: false
        };
    }

    static contextType = RuntimeContext;

    componentDidMount() {
        const {
            getManyLearningAssets,
            getManyAssetReviewItems,
            getManyGlobalSectionItems,
            getLearningFramework,
            isWelcomeAsset,
            currentLearningAsset = {},
            match: { params = {} },
            type = '',
            dirty,
            setIsFormDirty,
            getManyCollections
        } = this.props;
        const promises = [
            getLearningAssetStatus(
                params.id,
                {
                    pathsContext: this.context.pathsWeb,
                    ...(isWelcomeAsset ? { isWelcomeAsset } : {})
                }
            ),
            getManyLearningAssets({ pathsContext: this.context.pathsWeb }),
            getManyCollections({ pathsContext: this.context.pathsWeb }),
            getLearningFramework({ getFullObjects: true, pathsContext: this.context.pathsWeb }),
            getManyAssetReviewItems({ learningAssetId: params.id, pathsContext: this.context.pathsWeb }),
            getManyGlobalSectionItems({ pathsContext: this.context.pathsWeb })
        ];

        setIsFormDirty(dirty);
        const isCourseType = isLearningAssetCourseType(type);
        let participantsDataSource;
        if (isCourseType) {
            participantsDataSource = this._getParticipantsDataSource(type, currentLearningAsset.participants);
        }

        return BluebirdPromise.all(promises).then(([learningAssetStatus]) => {
            this.setState((prevState) => ({
                currentLearningAssetStatus: learningAssetStatus,
                assetType: type,
                participantsDataSource: participantsDataSource || prevState.participantsDataSource
            }));
        });
    }

    componentDidUpdate(prevProps) {
        const { dirty, setIsFormDirty } = this.props;
        if (dirty !== prevProps.dirty) {
            setIsFormDirty(dirty);
        }
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { initialValues, pristine } = this.props;
        if (
            this.props.match &&
            nextProps.match &&
            (this.props.match.params.id !== nextProps.match.params.id ||
                this.props.match.params.sectionitemid !== nextProps.match.params.sectionitemid)
        ) {
            if (
                this._getBasePath(nextProps) !== this._getBasePath(this.props) ||
                this.props.match.params.sectionitemid !== nextProps.match.params.sectionitemid
            ) {
                this._setNewPaths(nextProps);
            }
        }

        if (!this.props.isWelcomeAsset && nextProps.isWelcomeAsset) {
            this._setNewPaths(nextProps);
        }

        if (initialValues.createdBy !== nextProps.initialValues.createdBy) {
            return this.props.initialize(nextProps.initialValues);
        }

        if (
            pristine &&
            initialValues.sections &&
            nextProps.initialValues.sections &&
            !isEqual(initialValues.sections, nextProps.initialValues.sections)
        ) {
            return this.props.initialize(nextProps.initialValues);
        }

        if (!pristine && !initialValues.sections && nextProps.initialValues.sections) {
            return this.props.initialize(nextProps.initialValues);
        }

        if (!pristine && !initialValues.type && nextProps.initialValues.type) {
            return this.props.initialize(nextProps.initialValues);
        }

        if (!initialValues.abilityId && nextProps.initialValues.abilityId) {
            return this.props.initialize(nextProps.initialValues);
        }

        return null;
    }

    _setNewPaths = (props) => {
        const newPaths = this.state.availableRoutes.map((item) => {
            item.path = `${this._getBasePath(props)}${item.relativePath}`;
            return item;
        });
        this.setState({ availableRoutes: newPaths });
    };

    /**
     * Private Methods
     */

    /**
     *
     * @param {String} property
     * @return {*}
     * @private
     */
    _getFormValue(property) {
        return get(this.props.currentValues, `${property}`, '');
    }

    /**
     *
     * @param {Object} props
     * @param {String} props.type
     * @param {boolean} props.isNew
     * @return {String}
     * @private
     */
    _getBasePath = (props) => {
        const { isWelcomeAsset, isNew } = props;
        const path = isWelcomeAsset ? 'welcome' : 'learning';
        let relativePath = `/new${path}asset`;
        if (!isNew) {
            relativePath = `/edit${path}asset/${props.match.params.id}`;
        }

        return relativePath;
    };

    /**
     * Handles when a sectionItem is selected
     * @param {String} selectedSectionId
     * @param {String} selectedSectionItemId
     * @private
     */
    _sectionItemSelected = (selectedSectionId, selectedSectionItemId) => {
        this.setState({
            currentSectionId: selectedSectionId,
            selectedSectionItemId,
            selectedSectionId: ''
        });
    };

    /**
     * Handles when a section is selected
     * @param {String} selectedSectionId
     * @private
     */
    _sectionSelected = (selectedSectionId) => {
        this.setState({
            currentSectionId: '', // this field is used to accompany the selectedSectionItemId
            selectedSectionItemId: '',
            selectedSectionId
        });
    };

    /**
     * Handles when a section is deleted
     * @param {String} sectionName
     * @param {number} sectionIndex
     * @private
     */
    _deleteSection = (sectionName, sectionIndex) => {
        this.props.showPromptModal({
            modalType: MODALS.PROMPT_MODAL,
            modalProps: {
                title: `Delete Section: ${sectionName}`,
                promptText: 'Are you sure you want to delete the section?',
                confirmationButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                handleButtonClick: this.handleDeleteSectionModalResult.bind(this, 'sections', sectionIndex)
            }
        });
    };

    /**
     * Handles when a section is cloned
     * @param {Object} section
     * @param {number} index
     * @private
     */
    _copySection = (section, sectionLength) => {
        const copiedSection = { ...section };
        const newSectionIndex = sectionLength;

        const newSectionId = uuid();
        copiedSection.id = newSectionId;
        copiedSection.name = `${copiedSection.name} - COPIED`;
        const newSectionItems = [];
        copiedSection.sectionItems.forEach((item) => {
            const newItem = { ...item };
            newItem.id = uuid();
            newItem.sectionId = newSectionId;
            newSectionItems.push(newItem);
        });

        this.props.addToArray({ field: 'sections', value: copiedSection });
        this.props.changeFormValue({ field: `sections[${newSectionIndex}].sectionItems`, value: newSectionItems });
    };

    /**
     * Handles state management for global add section success message
     * @param {String} name
     * @param {Boolean} showMessage
     * @private
     */
    _displayGlobalAddSuccessMessage = (name, showMessage) =>
        this.setState({
            globalAddSuccessMessage: `"${name}" has been successfully added as a Global Section Item!`,
            globalSubmitSucceeded: showMessage
        });

    /**
     * Handles request to convert section item to global section item
     * @param {Object} sectionItemValues
     * @private
     */
    _saveSectionItemAsGlobalSectionItem = (currentSection, { isActive, isRequired, type, name, content }) => {
        const { postGlobalSectionItem, changeFormValue } = this.props;

        // only pass on keys needed for Global Section Items
        const filteredItem = {
            isActive,
            isRequired,
            type,
            name,
            content,
            isPublished: true
        };
        filteredItem.isPublished = true;
        return postGlobalSectionItem({ body: filteredItem, pathsContext: this.context.pathsWeb }).then(({ _id }) => {
            this._displayGlobalAddSuccessMessage(filteredItem.name, true, _id);
            changeFormValue({ field: `${currentSection}.globalSectionItemId`, value: _id });
            setTimeout(() => {
                this._displayGlobalAddSuccessMessage('', false, _id);
            }, 2000);
        });
    };

    /**
     * Handles when a sectionItem is deleted
     * @param {String} sectionItemName
     * @param {String} sectionItemField
     * @param {number} sectionItemIndex
     * @private
     */
    _deleteSectionItem = (sectionItemName, sectionItemField, sectionItemIndex) => {
        this.props.showPromptModal({
            modalType: MODALS.PROMPT_MODAL,
            modalProps: {
                className: 'delete-section-item-modal admin-modal',
                title: `Delete Section Item: ${sectionItemName}`.toLocaleUpperCase(),
                promptText: `Deleting this section item will also remove all user responses. We recommend making it inactive instead. Are you sure to delete this section item?`,
                confirmationButtonText: 'Delete',
                cancelButtonText: 'Cancel',
                handleButtonClick: this.handleDeleteSectionModalResult.bind(this, sectionItemField, sectionItemIndex)
            }
        });
    };

    /**
     *
     * @param {String} field
     * @param {number} indexToDelete
     * @param {boolean} value
     */
    handleDeleteSectionModalResult = (field, indexToDelete, value) => {
        const { selectedSectionItemId } = this.state;
        const { reviewItems = {}, putManyAssetReviewItems } = this.props;
        this.props.hideModal();
        if (value) {
            this.props.removeFromArray({ field, index: indexToDelete });

            if (reviewItems[selectedSectionItemId]) {
                // resolved associated asset review items
                putManyAssetReviewItems({
                    sectionItemId: selectedSectionItemId,
                    body: { status: 'RESOLVED' },
                    pathsContext: this.context.pathsWeb
                });
            }
        }
    };

    /**
     *
     * @param {String} field
     * @param {number} indexA
     * @param {number} indexB
     */
    swapItem = (field, indexA, indexB) => {
        this.props.swapItem({ field, indexA, indexB });
    };

    /**
     * Returns true if the type would be displayed as an activity
     * @param type
     * @return {boolean}
     * @private
     */
    _isActivityItem = (type) =>
        type === LEARNING_ASSET_TYPES.ACTIVITY ||
        type === LEARNING_ASSET_TYPES.VIDEO ||
        type === LEARNING_ASSET_TYPES.ARTICLE ||
        type === LEARNING_ASSET_TYPES.WELCOME;

    // eslint-disable-next-line consistent-return
    _createAssetHTML = () => {
        const { currentLearningAsset } = this.props;
        this.setState({ exporting: true, exportStatus: 'Exporting...' });
        const formattedAssetName = currentLearningAsset.name.replace(/ /g, '_').toUpperCase();
        const failedFileNames = [];
        let filesToExport;
        let mediaLinksToExport;
        if (currentLearningAsset && currentLearningAsset._id) {
            return clearS3Folder({
                Prefix: formattedAssetName,
                Bucket: `sip-${
                    this.context.pathsWeb.environment === 'local' ? 'dev' : this.context.pathsWeb.environment
                }-content-exports`,
                pathsContext: this.context.pathsWeb
            })
                .then(() =>
                    getLearningAssetsHTMLExports({
                        learningAssetId: currentLearningAsset._id,
                        pathsContext: this.context.pathsWeb
                    })
                )
                .then(({ files, mediaLinks }) => {
                    filesToExport = files;
                    mediaLinksToExport = mediaLinks;
                    const promises = [];
                    filesToExport.forEach((file) => {
                        promises.push(htmlFilePost(this.context.pathsWeb, file, 'content-exports'));
                    });
                    mediaLinksToExport.images.forEach((mediaUrl, index) => {
                        promises.push(
                            htmlMediaFilePost(
                                this.context.pathsWeb,
                                mediaUrl,
                                `${formattedAssetName}/media/images/${index}${path.extname(mediaUrl)}`,
                                'content-exports'
                            )
                        );
                    });
                    this.setState({
                        exporting: true
                    });
                    return Promise.all(promises);
                })
                .then((filesExportingResponses) => {
                    filesExportingResponses.forEach((response) => {
                        if (response.status !== 200) {
                            const splittedStringArray = response.url.split('/');
                            const failedFile = splittedStringArray[splittedStringArray.length - 1].split('?')[0];
                            failedFileNames.push(failedFile);
                        }
                    });
                })
                .then(() => {
                    const exportStatus = failedFileNames.length
                        ? `Export Incomplete. Failed files: ${failedFileNames.join(', ')}`
                        : 'Export Complete!';
                    this.setState({ exporting: false, exportStatus, failToExportFiles: failedFileNames });
                })
                .catch((error) => {
                    this.setState({ exporting: false, exportStatus: 'Error exporting.' });
                    console.error(error);
                });
        }
    };

    _getParticipantsDataSource = (type, participants) => {
        const courseAndImpactProjectParticipantTypes = [
            { label: 'Employee', value: 'employee' },
            { label: 'Social Entrepreneur', value: 'socialentrepreneur' },
            { label: 'All Participants', value: 'all' }
        ];

        const feedbackParticipantTypes = [
            { label: 'Employee', value: 'employee' },
            { label: 'Colleague', value: 'colleague' }
        ];

        if (type === LEARNING_ASSET_TYPES.FEEDBACK) {
            return feedbackParticipantTypes;
        }

        if (type === LEARNING_ASSET_TYPES.REFLECTION) {
            return participants.map((participantType) => {
                if (participantType.type === 'all') {
                    return { value: 'all', label: 'All Participants' };
                }
                return { value: participantType.type, label: participantType.type };
            });
        }

        return courseAndImpactProjectParticipantTypes;
    };

    _handleAssetTypeOnChange = (e, newAssetType, currentAssetType) => {
        const { currentValues, changeFormValue } = this.props;

        // update participants options
        const participantsDataSource = this._getParticipantsDataSource(newAssetType);
        return this.setState(
            {
                participantsDataSource,
                assetType: newAssetType
            }
        );
    };

    summaryTab = () => (
        <SummaryTab
            isVerbAdmin={this.props.isVerbAdmin}
            isContentAuthor={this.props.isContentAuthor}
            dimensions={this.props.dimensions}
            behaviors={this.props.behaviors}
            abilities={this.props.abilities}
            behaviorsById={this.props.behaviorsById}
            dimensionsById={this.props.dimensionsById}
            abilitiesById={this.props.abilitiesById}
            changeFormValue={this.props.changeFormValue}
            assetTypeOptions={this.props.assetTypeOptions}
            currentLearningAsset={this.props.currentLearningAsset}
            currentLearningAssetStatus={this.state.currentLearningAssetStatus}
            assetType={this.state.assetType}
            participantsDataSource={this.state.participantsDataSource}
            loggedInUser={this.props.loggedInUser}
        />
    );

    promptsTab = () => (
        <FieldArray
            name={'prompts'}
            component={PromptsTab}
            errors={this.props.error}
            prompts={this.props.currentLearningAsset.prompts}
            currentFormValues={this.props.prompts}
            changeFormValue={this.props.changeFormValue}
        />
    );

    resolveComment = (review) => {
        const { putAssetReviewItem } = this.props;
        const updateObject = {};
        if (review.status === 'RESOLVED') {
            updateObject.status = 'OPEN';
        } else {
            updateObject.status = 'RESOLVED';
        }
        updateObject.revision = review.revision;
        return putAssetReviewItem({
            assetReviewItemId: review._id,
            body: updateObject,
            pathsContext: this.context.pathsWeb
        });
    };

    deleteComment = (review) => {
        const { putAssetReviewItem } = this.props;
        return putAssetReviewItem({
            assetReviewItemId: review._id,
            body: { isDeleted: true, revision: review.revision },
            pathsContext: this.context.pathsWeb
        });
    };

    handleAssetReviewItemSubmit = (assetReviewItem) => {
        const { postAssetReviewItem, currentLearningAsset } = this.props;
        const replyBody = {
            parentId: currentLearningAsset._id,
            parentType: 'LEARNING_ASSET',
            ...assetReviewItem
        };

        return postAssetReviewItem({ body: replyBody, pathsContext: this.context.pathsWeb });
    };

    getAssetReviewItemsForSectionItem = (currentSectionItemId) => {
        const { reviewItems = {} } = this.props;
        const assetReviewItemsForCurrentSectionItem = Object.values(reviewItems[currentSectionItemId] || {})
            .sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn))
            .reduce((acc, item) => {
                if (item.parentCommentId) {
                    if (acc[item.parentCommentId]) {
                        acc[item.parentCommentId].replyItems.push(item);
                    }
                } else {
                    acc[item._id] = { ...item, replyItems: [] };
                }
                return acc;
            }, {});
        return Object.values(assetReviewItemsForCurrentSectionItem);
    };

    commentsTab = () => {
        const {
            match: {
                params: { sectionitemid, id }
            },
            currentValues,
            assetLinkOptions,
            changeFormValue,
            sectionsByItemId,
            globalSectionItems,
            currentLearningAsset,
            loggedInUserId,
            getLearningAsset,
            isContentAdmin
        } = this.props;
        const userReviews = this.getAssetReviewItemsForSectionItem(sectionitemid);
        const currentSectionId = sectionsByItemId[sectionitemid][0];
        const currentSectionItem = sectionsByItemId[sectionitemid][1];

        return (
            <CommentsErrorBoundary>
                <RenderComments
                    userReviews={userReviews}
                    onResolved={this.resolveComment}
                    assetLinkOptions={assetLinkOptions}
                    currentValues={currentValues}
                    type={this.props.type}
                    learningAssetId={id}
                    currentSectionItem={currentSectionItem}
                    selectedSectionId={currentSectionId}
                    selectedSectionItemId={sectionitemid}
                    swapItem={this.swapItem}
                    globalSectionItems={globalSectionItems}
                    onDeleteSectionItem={this._deleteSectionItem}
                    changeFormValue={changeFormValue}
                    currentLearningAsset={currentLearningAsset}
                    loggedInUserId={loggedInUserId}
                    getLearningAsset={getLearningAsset}
                    handleAssetReviewItemSubmit={this.handleAssetReviewItemSubmit}
                    onDelete={this.deleteComment}
                    isContentAdmin={isContentAdmin}
                />
            </CommentsErrorBoundary>
        );
    };

    /**
     * Builder Tab
     * @return {XML}
     */
    builderTab = () => {
        let displayForm = null;
        const {
            currentValues,
            reviewItems = {},
            globalSectionItems,
            getLearningAsset,
            currentLearningAsset,
            isContentAuthor,
            collectionsByLearningAssetId
        } = this.props;
        const basePath = this._getBasePath(this.props);
        if (get(this.state, 'selectedSectionId', '') !== '') {
            const { selectedSectionId } = this.state;
            displayForm = this._generateSectionForm(selectedSectionId);
        }

        if (get(this.state, 'selectedSectionItemId', '') !== '') {
            const { changeFormValue, assetLinkOptions } = this.props;
            const {
                currentSectionId,
                selectedSectionItemId,
                globalAddSuccessMessage,
                globalSubmitSucceeded
            } = this.state;
            displayForm = (
                <SectionItemFormSection
                    assetLinkOptions={assetLinkOptions}
                    currentValues={currentValues}
                    type={this.props.type}
                    selectedSectionId={currentSectionId}
                    selectedSectionItemId={selectedSectionItemId}
                    swapItem={this.swapItem}
                    onDeleteSectionItem={this._deleteSectionItem}
                    onGlobalSaveSectionItem={this._saveSectionItemAsGlobalSectionItem}
                    changeFormValue={changeFormValue}
                    globalSectionItems={globalSectionItems}
                    globalAddSuccessMessage={globalAddSuccessMessage}
                    globalSubmitSucceeded={globalSubmitSucceeded}
                    getLearningAsset={getLearningAsset}
                    hideForContentAuthor={isContentAuthor}
                    collectionsByLearningAssetId={collectionsByLearningAssetId}
                />
            );
        }
        return (
            <section className={'row m-0'}>
                <div className={cns('col-md-3', 'trackSectionPanel', 'track-tab-spacing')}>
                    <h4>{currentLearningAsset.name} </h4>
                    <section className={'pagePanel'}>
                        <FieldArray
                            name={'sections'}
                            type={this.props.type}
                            component={RenderSections}
                            currentFormValues={currentValues}
                            sectionSelected={this._sectionSelected}
                            selectedSectionId={this.state.selectedSectionId}
                            sectionItemSelected={this._sectionItemSelected}
                            selectedSectionItemId={this.state.selectedSectionItemId}
                            reviewItems={reviewItems}
                            basePath={basePath}
                            hideForContentAuthor={isContentAuthor}
                            changeFormValue={this.props.changeFormValue}
                        />
                    </section>
                </div>
                <div className={cns('col-9', 'trackSectionBuilder', 'track-body-spacing')}>{displayForm}</div>
            </section>
        );
    };

    _hasOnlyOneSection = () => {
        const sections = this._getFormValue(SECTIONS_KEY);

        return get(sections, 'length', 0) <= 1;
    };

    /**
     *
     * @param selectedSectionId
     * @return {XML}
     * @private
     */
    _generateSectionForm = (selectedSectionId) => {
        const { type } = this.props;
        const SECTIONS_KEY = 'sections';
        const sections = this._getFormValue(SECTIONS_KEY);
        const SECTION_LENGTH = sections.length;
        const index = findIndex(sections, { id: selectedSectionId });
        const currentSection = sections[index];
        const sectionName = this._getFormValue(`${SECTIONS_KEY}[${index}].name`);
        const isCourseType = isLearningAssetCourseType(type);

        const canSectionMoveUp = index - 1 >= 0;
        const canSectionMoveDown = index + 1 < SECTION_LENGTH;
        return (
            <div>
                <Row>
                    <div className={'col-md-8'}>
                        <h4>{sectionName}</h4>
                    </div>
                    <div className={'col-md-4'}>
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-trash'}
                            className={'ms-auto'}
                            disabled={this._hasOnlyOneSection()}
                            onClick={() => {
                                this._deleteSection(sectionName, index);
                            }}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-clone'}
                            className={'ms-auto'}
                            onClick={() => {
                                this._copySection(currentSection, SECTION_LENGTH);
                            }}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-angle-down'}
                            className={'ms-auto'}
                            disabled={!canSectionMoveDown}
                            onClick={() => {
                                this.swapItem(SECTIONS_KEY, index, index + 1);
                            }}
                        />
                        <VerbButton
                            buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                            fontAwesomeIcon={'fa-angle-up'}
                            className={'ms-auto'}
                            disabled={!canSectionMoveUp}
                            onClick={() => {
                                this.swapItem(SECTIONS_KEY, index, index - 1);
                            }}
                        />
                    </div>
                </Row>
                <Row>
                    <Field
                        name={`${SECTIONS_KEY}[${index}].name`}
                        label={'Name'}
                        className={'col-md-12'}
                        defaultValue={'NAME'}
                        component={BasicText}
                    />
                </Row>
                {isCourseType ? (
                    <Fragment>
                        <Row>
                            <Field
                                name={`${SECTIONS_KEY}[${index}].isIncludedInCompletionRate`}
                                className={'col-md-12'}
                                label={'Is included in completion rate?'}
                                component={BasicToggle}
                                defaultValue={true}
                                id={'is-included-in-completion-rate-toggle'}
                            />
                        </Row>
                        <Row>
                            <Field
                                name={`${SECTIONS_KEY}[${index}].description`}
                                label={'Description'}
                                className={'col-md-12'}
                                component={BasicTextArea}
                            />
                        </Row>
                        <Row>
                            <Field
                                name={`${SECTIONS_KEY}[${index}].sectionIcon`}
                                label={'Section Icon'}
                                className={'col-md-6'}
                                options={sectionIcons}
                                component={BasicSelect}
                            />
                        </Row>
                        <Row>
                            <FieldArray
                                name={`${SECTIONS_KEY}[${index}].resources`}
                                label={'Section Resources'}
                                className={'col-md-6 '}
                                component={this._renderSectionResources.bind(this, this.props, this.state)}
                            />
                        </Row>
                    </Fragment>
                ) : null}
            </div>
        );
    };

    _renderSectionResources = (props, state, { fields }) => {
        let table;
        if (fields && fields.length) {
            table = (
                <Table striped={false} bordered={false} hover={true}>
                    <thead>
                        <tr>
                            <th className={'buttonCell'}>Actions</th>
                            <th>Resource Title</th>
                            <th>Resource URL</th>
                            <th hidden />
                        </tr>
                    </thead>
                    <tbody>
                        {fields.map((resource, index) => (
                            <tr key={index}>
                                <td className={'buttonCell'}>
                                    <VerbButton
                                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DETAILS}
                                        fontAwesomeIcon={'fa-trash'}
                                        title={'Remove Resource'}
                                        onClick={() => fields.remove(index)}
                                    />
                                </td>
                                <td>
                                    <Field name={`${resource}.title`} component={DisplayOnlyInput} />
                                </td>
                                <td>
                                    <Field name={`${resource}.url`} component={DisplayOnlyInput} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            );
        }

        return (
            <div className={'url-resource-form'}>
                <BasicUrlResource handleSubmit={this._handleUrlSubmit.bind(this, fields)} label={'SECTION RESOURCES'} />
                {table}
            </div>
        );
    };

    _handleUrlSubmit = (fields, values) => {
        const { newResourceTitle, newResourceUrl, openInNewTab } = values;
        if (newResourceTitle && newResourceUrl) {
            fields.push({
                title: newResourceTitle,
                url: newResourceUrl,
                openInNewTab
            });
        }
    };

    /**
     *
     * @param {Object} values
     * @private
     */
    _saveOnly = (values, dispatch, props) => {
        const { cleanPrompts, isWelcomeAsset, initialize } = this.props;
        const cleanedValues = cleanPrompts({ ...values });
        const learningAsset = Object.assign({ type: props.type }, cleanedValues);

        const masterValidation = masterValidate(learningAsset);
        if (masterValidation.fault === 0) {
            this.setState({
                formSaving: true,
                masterValidationMessages: []
            });
            return this.props
                .onSubmit(false, values, initialize)
                .then((savedLearningAsset) =>
                    getLearningAssetStatus(
                        savedLearningAsset._id,
                        {
                            pathsContext: this.context.pathsWeb,
                            ...(isWelcomeAsset ? { isWelcomeAsset } : {})
                        }
                    )
                )
                .then((learningAssetStatus) => {
                    this.setState({ currentLearningAssetStatus: learningAssetStatus });
                })
                .finally(() => {
                    this.setState({ formSaving: false });
                });
        }
        this.setState({
            masterValidationMessages: masterValidation.messages.map((message, i) => (
                <p key={i} className={'master-validation-messages'}>
                    {message}
                </p>
            ))
        });
        return null;
    };

    /**
     *
     * @param {Object} values
     * @private
     */
    _saveAndExit = (values, dispatch, props) => {
        const learningAsset = Object.assign({ type: props.type }, values);
        const masterValidation = masterValidate(learningAsset);

        if (masterValidation.fault === 0) {
            this.setState({
                formSaving: true,
                masterValidationMessages: []
            });
            return this.props.onSubmit(true, values).catch(() => {
                this.setState({ formSaving: false });
            });
        }
        this.setState({
            masterValidationMessages: masterValidation.messages.map((message, i) => (
                <p key={i} className={'master-validation-messages'}>
                    {message}
                </p>
            ))
        });
        return null;
    };

    /**
     * @returns {JSX}
     *
     * this func returns a popover that (hard-codedly) restricts prod environment's content export to only WFM's learningAssets.
     */
    _makeExportSubmissionPopover = () => {
        const wfmCompanyId = '5c2fbff21f0af30011e6d09f';
        const confirmButton = (
            <VerbButton
                fontAwesomeIcon={'fa-arrow-alt-circle-right'}
                className={'export-submit-button'}
                onClick={this._createAssetHTML}
            >
                YES
            </VerbButton>
        );

        let exportSubmissionMessage;
        const isLicensedForWFM = (this.state.currentLearningAssetStatus.licensedCompanies || []).some(
            (licensedCompany) => licensedCompany._id === wfmCompanyId
        );

        if (this.context.pathsWeb.environment === 'prod' && !isLicensedForWFM) {
            exportSubmissionMessage = (
                <p className={'export-status-message not-ready'}>
                    Learning asset must be licensed for Whole Foods Market
                </p>
            );
        } else {
            exportSubmissionMessage = (
                <p className={'export-status-message'}>{`Exporting this learning asset ${
                    this.context.pathsWeb.environment === 'prod' ? 'for Whole Foods Market' : ''
                }. Are you sure?`}</p>
            );
        }

        return (
            <Popover id={'export-submission-popover'} className={'export-submission-popover'}>
                {exportSubmissionMessage}
                {(isLicensedForWFM || this.context.pathsWeb.environment !== 'prod') && (
                    <div className={'export-submission-buttons-container'}>{confirmButton}</div>
                )}
            </Popover>
        );
    };

    render() {
        const { submitting, handleSubmit, onCancel, currentLearningAsset, isContentAuthor } = this.props;
        const { formSaving, masterValidationMessages, exporting, exportStatus, failToExportFiles } = this.state;
        const routes = this.state.availableRoutes.map((routeItem, index) => (
            <Route
                key={`${routeItem.relativePath}${index}`}
                path={routeItem.path}
                exact={routeItem.exact}
                render={routeItem.render}
            />
        ));
        const successMsg = masterValidationMessages.length > 0 ? masterValidationMessages : 'Activity Saved!';

        const archiveSubmissionPopover = this._makeExportSubmissionPopover();

        const exportButton = !isContentAuthor ? (
            <OverlayTrigger
                trigger={'click'}
                transition={false}
                placement={'top'}
                rootClose
                overlay={archiveSubmissionPopover}
            >
                <VerbButton
                    buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                    fontAwesomeIcon={'fa-arrow-alt-circle-right'}
                    textId={'EXPORT'}
                    defaultMessage={'EXPORT'}
                    disabled={submitting || formSaving}
                />
            </OverlayTrigger>
        ) : null;

        return (
            <form className={'trackForm'}>
                <div className={cns('col-12', 'trackSections')}>{routes}</div>
                <div className={cns('row', 'trackBtnRow')}>
                    <div className={'col-sm-3'}>
                        <FormFeedback className={'formFeedback'} successMsg={successMsg} {...this.props} />
                        {exporting || exportStatus ? (
                            <div
                                className={cns('row export-response', {
                                    complete: !failToExportFiles.length,
                                    incomplete: failToExportFiles.length
                                })}
                            >
                                {exportStatus}
                            </div>
                        ) : null}
                    </div>
                    <div className={'col-8'}>
                        <div className={'row'}>
                            <VerbButton
                                fontAwesomeIcon={'fa-save'}
                                textId={'BUTTON_SAVE'}
                                defaultMessage={'SAVE'}
                                onClick={handleSubmit(this._saveOnly)}
                                disabled={submitting || formSaving}
                            />
                            <VerbButton
                                fontAwesomeIcon={'fa-save'}
                                textId={'BUTTON_SAVE_AND_EXIT'}
                                defaultMessage={'SAVE & EXIT'}
                                onClick={handleSubmit(this._saveAndExit)}
                                disabled={submitting || formSaving}
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            />
                            <VerbButton
                                fontAwesomeIcon={'fa-eye'}
                                textId={'BUTTON_PREVIEW'}
                                defaultMessage={'PREVIEW'}
                                newTab={true}
                                onClick={() =>
                                    this.props.history.push(
                                        `/previewlearningasset/${
                                            currentLearningAsset.type === 'WELCOME' ? 'welcome' : 'learning'
                                        }/${currentLearningAsset._id}`
                                    )
                                }
                                disabled={submitting || formSaving || !currentLearningAsset._id}
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                            />
                            <VerbButton
                                buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                fontAwesomeIcon={'fa-ban'}
                                textId={'BUTTON_CANCEL'}
                                defaultMessage={'CANCEL'}
                                onClick={onCancel}
                                disabled={submitting || formSaving}
                            />
                            {exportButton}
                        </div>
                    </div>
                </div>
            </form>
        );
    }
}

const TrackV2ReduxForm = reduxForm({
    form: 'TrackV2',
    validate: (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = 'Name is a required field';
            errors._error = 'Required field not filled';
        }
        if (!values.subtitle) {
            errors.subtitle = 'Subtitle is a required field';
            errors._error = 'Required field not filled';
        }

        if (values.videoMinutes && !Number.isInteger(values.videoMinutes)) {
            errors.videoMinutes = 'Video minutes must be a whole number.';
            errors._error = 'Required field not filled';
        }

        if (values.audioMinutes && !Number.isInteger(values.audioMinutes)) {
            errors.audioMinutes = 'Audio minutes must be a whole number.';
            errors._error = 'Required field not filled';
        }

        if (values.prompts && values.prompts.length > 0) {
            values.prompts.forEach(({ value, showOption }) => {
                if ((!value || !showOption) && values.prompts.length > 1) {
                    errors.prompts = 'Please fill out all fields';
                    errors._error = 'Required prompt not filled';
                }
            });
        }

        // Uncomment when new framework is live
        // if (!values.behaviorId || !values.behaviorId.length) {
        //   errors.behaviorId = 'You must select a behavior';
        //   errors._error = 'Required field not filled';
        // }
        localStorage.setItem(`assetBackup`, JSON.stringify(values));

        return errors;
    }
})(TrackV2Form);

const makeMapStateToProps = () => {
    const makeReviewItemsBySectionItem = makeGetObjectByKeyArrays(`assetReviewItems`);
    const getGlobalSectionItemsObjectsFromStateById = makeGetObjectsFromStateByFunction('globalSectionItems.byId');
    const filterFunction = (globalSectionItem) => {
        if (globalSectionItem.isPublished && !globalSectionItem.isDeleted) {
            return true;
        }

        return false;
    };

    return (state, ownProps) => {
        const {
            auth: { isVerbAdmin, isContentAuthor, loggedInUserId, loggedInUser, isContentAdmin },
            learningAssets = {},
            welcomeAssets = {},
            dimensions = {},
            learningFrameworks = {},
            abilities = {},
            behaviors = {},
            collections = {}
        } = state;
        const learningAssetsById = learningAssets.byId || {};
        const welcomeAssetsById = welcomeAssets.byId || {};
        const collectionsById = collections.byId || {};
        const dimensionsStateById = dimensions.byId || {};
        const learningFrameworksById = learningFrameworks.byId || {};
        const abilitiesStateById = abilities.byId || {};
        const behaviorsStateById = behaviors.byId || {};
        const globalSectionItems = getGlobalSectionItemsObjectsFromStateById(state, filterFunction);
        const { match } = ownProps;
        const assetLinkOptions = Object.values(learningAssetsById).map((asset) => ({
            label: asset.name,
            value: asset._id
        }));
        const id = get(match, 'params.id', '');
        let currentLearningAsset = learningAssetsById[id] || welcomeAssetsById[id] || {};
        if (ownProps.isNew) {
            currentLearningAsset = ownProps.track;
        }
        const reviewItems = makeReviewItemsBySectionItem(state, `byLearningAssetId.${id}`, 'sectionItemId');

        const newAssetSelectOptions = {};
        Object.values(collectionsById).forEach((collection) => {
            const collectionName = collection.name;
            const { contentIds = [] } = collection;

            contentIds.forEach((contentId) => {
                if (learningAssetsById[contentId.id]) {
                    const learningAssetName = learningAssetsById[contentId.id].name;
                    if (Array.isArray(newAssetSelectOptions[contentId.id])) {
                        newAssetSelectOptions[contentId.id].push({
                            label: `${collectionName} - ${learningAssetName}`,
                            value: learningAssetsById[contentId.id]._id
                        });
                    } else {
                        newAssetSelectOptions[contentId.id] = [];
                        newAssetSelectOptions[contentId.id].push({
                            label: `${collectionName} - ${learningAssetName}`,
                            value: learningAssetsById[contentId.id]._id
                        });
                    }
                }
            });
        });

        const {
            name,
            subtitle,
            badge,
            type,
            description,
            durationId = 'f8fc05f4339b43499728617b6df30ac9',
            syllabus,
            adminDescription,
            coverImageUrl = attachCloudinaryOptionsToImageSource(DEFAULT_IMAGE_URLS.LEARNING_ASSET_COVER_IMAGE, {
                color: '39236D'
            }),
            thumbnailImageUrl = DEFAULT_IMAGE_URLS.LEARNING_ASSET_THUMBNAIL_IMAGE,
            openNavigation = false,
            sections,
            sectionItems,
            participants = [{ id: uuid(), type: 'all' }],
            isActive = false,
            isImpact = false,
            author,
            globalSectionItemId,
            rank = 10,
            subscriptionType = LICENSE_TYPES.AVAILABLE_FOR_ALL,
            scheduleType = 'selfpaced',
            level = 1,
            videoMinutes = 0,
            audioMinutes = 0,
            searchTerms = [],
            dimensionId,
            behaviorId,
            abilityId,
            allowResubscribe,
            metadataTags = [],
            typeLabel,
            prompts = [{ value: '', showOption: 'SHOWN_TO_ALL' }],
            createdBy = CREATED_BY_TYPES.BY_VERB,
            hideQuestionAndResultsHeaders = false
        } = currentLearningAsset;
        const assetTypeOptions = generateAssetTypeOptions(type);
        const sectionsByItemId = sections
            ? sections.reduce((acc, section) => {
                  if (section.sectionItems) {
                      for (let i = 0; i < section.sectionItems.length; i += 1) {
                          acc[section.sectionItems[i].id] = [section.id, section.sectionItems[i]];
                      }
                  }
                  return acc;
              }, {})
            : {};

        let defaultLearningFramework = { dimensionIds: [] };
        const frameworkIds = Object.keys(learningFrameworksById) || [];
        for (let i = 0; i < frameworkIds.length; i += 1) {
            if (!defaultLearningFramework._id && learningFrameworksById[frameworkIds[i]]) {
                defaultLearningFramework = learningFrameworksById[frameworkIds[i]];
            }
        }
        const dimensionsById = {};
        const defaultDimensionIds = defaultLearningFramework.dimensionIds || [];
        defaultDimensionIds.forEach((id) => {
            if (dimensionsStateById[id]) {
                dimensionsById[id] = dimensionsStateById[id];
            }
        });

        const abilitiesById = {};
        Object.keys(abilitiesStateById).forEach((id) => {
            if (defaultDimensionIds.indexOf(abilitiesStateById[id].dimensionId) > -1) {
                abilitiesById[id] = abilitiesStateById[id];
            }
        });
        return {
            currentValues: getFormValues('TrackV2')(state),
            dirty: isDirty('TrackV2'),
            behaviors: Object.values(behaviorsStateById),
            abilities: Object.values(abilitiesById),
            dimensions: Object.values(dimensionsById),
            behaviorsById: behaviorsStateById,
            abilitiesById,
            dimensionsById,
            currentLearningAsset,
            initialValues: {
                name,
                type,
                subtitle,
                badge,
                description,
                adminDescription,
                durationId,
                scheduleType,
                syllabus,
                behaviorId,
                abilityId,
                dimensionId,
                coverImageUrl,
                thumbnailImageUrl,
                openNavigation,
                isActive,
                isImpact,
                rank,
                subscriptionType: LICENSE_TYPES.AVAILABLE_FOR_ALL,
                sections,
                sectionItems,
                participants,
                author,
                level,
                videoMinutes,
                audioMinutes,
                searchTerms,
                allowResubscribe,
                metadataTags: metadataTags.map((tag) => {
                    const currentMetadata = ASSET_METADATA_LOOKUP[tag.id];
                    return {
                        ...tag,
                        label: `${ASSET_METADATA_CATEGORIES[currentMetadata.categoryId].label}: ${
                            currentMetadata.label
                        }`
                    };
                }),
                prompts,
                typeLabel,
                createdBy: CREATED_BY_TYPES.BY_VERB,
                hideQuestionAndResultsHeaders
            },
            prompts,
            sectionsByItemId,
            isVerbAdmin,
            isContentAuthor,
            isContentAdmin,
            assetLinkOptions,
            globalSectionItems,
            globalSectionItemId,
            reviewItems,
            assetTypeOptions,
            loggedInUserId,
            loggedInUser,
            collectionsByLearningAssetId: newAssetSelectOptions
        };
    };
};

const mapDispatchToProps = (dispatch) => ({
    submitForm: () => dispatch(submit('TrackV2')),
    swapItem: (params) => dispatch(arraySwap('TrackV2', params.field, params.indexA, params.indexB)),
    removeFromArray: (params) => dispatch(arrayRemove('TrackV2', params.field, params.index)),
    addToArray: (params) => dispatch(arrayPush('TrackV2', params.field, params.value)),
    changeFormValue: (params) => dispatch(change('TrackV2', params.field, params.value)),
    initialize: (params) => dispatch(initialize('TrackV2', params.data)),
    showPromptModal: (params) => dispatch(showModal(params)),
    hideModal: () => dispatch(hideModal()),
    getManyLearningAssets: (params) => dispatch(getManyLearningAssets(params)),
    getLearningAsset: (params) => dispatch(getLearningAsset(params)),
    getManyAssetReviewItems: (params) => dispatch(getManyAssetReviewItems(params)),
    putAssetReviewItem: (params) => dispatch(putAssetReviewItem(params)),
    putManyAssetReviewItems: (params) => dispatch(putManyAssetReviewItems(params)),
    getManyGlobalSectionItems: (params) => dispatch(getManyGlobalSectionItems(params)),
    postAssetReviewItem: (params) => dispatch(postAssetReviewItem(params)),
    getLearningFramework: (params) => dispatch(getLearningFramework(params)),
    postGlobalSectionItem: (params) => dispatch(postGlobalSectionItem(params)),
    getManyCollections: (params) => dispatch(getManyCollections(params))
});

export const RawTrackV2FormComponent = TrackV2Form;
export const TrackV2FormComponent = TrackV2ReduxForm;
export default withRouter(connect(makeMapStateToProps, mapDispatchToProps)(TrackV2ReduxForm));
